import type {ISbStoryParams} from 'storyblok-js-client';
import type {H3Event} from 'h3';
import {getSsrRequestEvent} from '#shared/utils/ssrRequestContext';
import {mergePreviewSearch, queryFromRequestUrl, storyblokPreviewParams, type StoryblokPreviewParams, type StoryblokPreviewSearch} from '#shared/utils/storyblokPreview';

function requestUrl(event: H3Event): string {
	const req = event.node?.req as {originalUrl?: string; url?: string} | undefined;
	// h3 keeps the incoming URL, including ?_storyblok, on originalUrl. event.path can
	// later be only the pathname, and getQuery() would then miss the preview params.
	return req?.originalUrl || req?.url || event.path || '';
}

function routeQuery(): StoryblokPreviewSearch | undefined {
	try {
		return useRoute().query as StoryblokPreviewSearch;
	} catch {
		return undefined;
	}
}

function requestQuery(event?: H3Event): StoryblokPreviewSearch | undefined {
	if (!import.meta.server) {
		return undefined;
	}

	const requestEvent = event ?? getSsrRequestEvent();
	let nuxtEvent: H3Event | undefined;

	if (!requestEvent) {
		try {
			nuxtEvent = useRequestEvent();
		} catch {
			// Request context is missing outside a Nitro event.
		}
	}

	const resolved = requestEvent ?? nuxtEvent;
	return resolved ? queryFromRequestUrl(requestUrl(resolved)) : undefined;
}

function getBaseParams(event?: H3Event): StoryblokPreviewParams {
	// Either the raw request URL or the vue route may carry ?_storyblok; a non-empty value wins.
	return storyblokPreviewParams(mergePreviewSearch(requestQuery(event), routeQuery()), {
		draftByDefault: process.env.NODE_ENV === 'development',
	});
}

export function getCommonStoryParams(event?: H3Event): ISbStoryParams {
	return {
		...getBaseParams(event),
		resolve_relations: [
			'BusinessUnitRentalList.items',
			'PageTypeRecipesRecipeDetail.author',
			'PageTypeRecipesRecipeDetail.demanding',
			'PageTypeRecipesRecipeDetail.author',
			'FeaturedRecipeAuthors.authors',
			'RecipeAuthorStack.authors',
			'PageTypeBlogArticleDetail.author',
			'CategoryStack.RecipeAuthorStack',
			'PageTypeRecipesRecipeDetail.newsletterSignupBanner',
			'PageTypeBlogArticleDetail.newsletterSignupBanner',
			'PageTypePressReleaseDetail.newsletterSignupBanner',
			'PageTypeRestaurant.newsletterSignupBanner',
			'PageTypeRestaurantMenu.newsletterSignupBanner',
			'PageTypeHomepage.newsletterSignupBanner',
			'PageTypeLeafletListing.newsletterSignupBanner',
			'PageTypeHomepage.campaignBanner',
			'HomepageIntroPersonalizedItem.hypermarket',
			'HomepageIntroSecondaryList.hypermarket',
			'MyGlobusPresentationSlideshowItem.hypermarket',
			'MyGlobusFaqSelector.MyGlobusFaqSelectorItems',
			'MyGlobusFAQ.moreFaq',
			'PageTypeForm.formForHypermarket',
			'FormSelect.selectedForm',
			'FormEmbed.selectedForm',
			'PageTypePressReleaseDetail.followUpTiles',
			'PageTypeRecipesRecipeDetail.followUpTiles',
			'PageTypeBlogArticleDetail.followUpTiles',
			'PageTypeLeafletListing.followUpTiles',
			'CampaignBannerSelector.campaignSelector',
		].join(','),
	};
}

export function getCommonStorySitemapParams() {
	return {
		...getBaseParams(),
		excluding_fields: 'body,openingHours,content',
	};
}
